export interface RecommendableVehicle {
  id: number;
  brand: string;
  model: string;
  price: number;
  modelYear?: number;
  fuel?: string;
  transmission?: string;
  type?: number;
  store?: string | number;
  changed?: { featured?: boolean };
}

export interface RelatedVehiclesOptions {
  limit?: number;
  filterByStore?: boolean;
  vehicleStoreId?: string | number;
  /** Max cards with the same brand+model (default: 2) */
  maxSameModel?: number;
}

interface ScoredVehicle {
  vehicle: RecommendableVehicle;
  score: number;
}

const WEIGHTS = {
  sameModel: 45,
  sameBrand: 28,
  sameType: 12,
  price: 20,
  fuel: 8,
  transmission: 6,
  year: 8,
  featured: 3,
} as const;

const normalize = (value?: string) => (value ?? '').trim().toLowerCase();

const modelKey = (vehicle: RecommendableVehicle) =>
  `${normalize(vehicle.brand)}\0${normalize(vehicle.model)}`;

const scorePriceSimilarity = (currentPrice: number, candidatePrice: number): number => {
  const basePrice = Math.max(currentPrice, 1);
  const priceDiffRatio = Math.abs(candidatePrice - currentPrice) / basePrice;

  if (priceDiffRatio <= 0.05) return WEIGHTS.price;
  if (priceDiffRatio <= 0.1) return WEIGHTS.price * 0.75;
  if (priceDiffRatio <= 0.2) return WEIGHTS.price * 0.5;
  if (priceDiffRatio <= 0.35) return WEIGHTS.price * 0.25;
  return 0;
};

const scoreYearProximity = (currentYear?: number, candidateYear?: number): number => {
  if (!currentYear || !candidateYear) return 0;

  const yearDiff = Math.abs(candidateYear - currentYear);
  if (yearDiff === 0) return WEIGHTS.year;
  if (yearDiff <= 2) return WEIGHTS.year * 0.625;
  if (yearDiff <= 5) return WEIGHTS.year * 0.25;
  return 0;
};

export const computeVehicleRelevanceScore = (
  current: RecommendableVehicle,
  candidate: RecommendableVehicle
): number => {
  let score = 0;

  const sameBrand = normalize(candidate.brand) === normalize(current.brand);
  const sameModel = sameBrand && normalize(candidate.model) === normalize(current.model);

  if (sameModel) {
    score += WEIGHTS.sameModel;
  } else if (sameBrand) {
    score += WEIGHTS.sameBrand;
  }

  score += scorePriceSimilarity(current.price, candidate.price);

  if (current.type !== undefined && candidate.type === current.type) {
    score += WEIGHTS.sameType;
  }

  if (current.fuel && candidate.fuel && normalize(candidate.fuel) === normalize(current.fuel)) {
    score += WEIGHTS.fuel;
  }

  if (
    current.transmission &&
    candidate.transmission &&
    normalize(candidate.transmission) === normalize(current.transmission)
  ) {
    score += WEIGHTS.transmission;
  }

  score += scoreYearProximity(current.modelYear, candidate.modelYear);

  if (candidate.changed?.featured) {
    score += WEIGHTS.featured;
  }

  return score;
};

const selectDiverseRecommendations = (
  scored: ScoredVehicle[],
  limit: number,
  maxSameModel: number
): RecommendableVehicle[] => {
  const selected: RecommendableVehicle[] = [];
  const usedIds = new Set<number>();
  const modelCounts = new Map<string, number>();

  const tryAdd = (vehicle: RecommendableVehicle) => {
    if (selected.length >= limit || usedIds.has(vehicle.id)) return false;

    const key = modelKey(vehicle);
    if ((modelCounts.get(key) ?? 0) >= maxSameModel) return false;

    selected.push(vehicle);
    usedIds.add(vehicle.id);
    modelCounts.set(key, (modelCounts.get(key) ?? 0) + 1);
    return true;
  };

  for (const { vehicle } of scored) {
    if (selected.length >= limit) break;
    tryAdd(vehicle);
  }

  return selected;
};

/**
 * Recommends vehicles using weighted relevance scoring with diversity caps.
 * Mixes same model, same brand, similar price, and comparable attributes.
 */
export const getRelatedVehicles = <T extends RecommendableVehicle>(
  currentVehicle: T,
  allVehicles: T[],
  options: RelatedVehiclesOptions = {}
): T[] => {
  const {
    limit = 4,
    filterByStore = false,
    vehicleStoreId,
    maxSameModel = 2,
  } = options;

  const candidates = allVehicles.filter((vehicle) => {
    if (vehicle.id === currentVehicle.id) return false;
    if (filterByStore && vehicleStoreId !== undefined && vehicle.store !== vehicleStoreId) {
      return false;
    }
    return true;
  });

  if (candidates.length === 0) return [];

  const scored: ScoredVehicle[] = candidates
    .map((vehicle) => ({
      vehicle,
      score: computeVehicleRelevanceScore(currentVehicle, vehicle),
    }))
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return Math.abs(a.vehicle.price - currentVehicle.price) -
        Math.abs(b.vehicle.price - currentVehicle.price);
    });

  return selectDiverseRecommendations(scored, limit, maxSameModel) as T[];
};
